"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { MARQUES, PACKS, SIGNATURE } from "@/data/catalog";
import { SITES } from "@/data/sites";
import { Accueil } from "./views-accueil";
import { SitePage } from "./views-sites";
import { MarquesView, MarqueDetail } from "./views-catalogue";
import { PacksView, SignatureView } from "./views-packs-signature";
import { PortailsView } from "./views-portails";
import { Crown, ProductLightbox, PanierDrawer, FlashToast } from "./ui-extras";
import { ChatbotImperial } from "./chatbot";
import type { View, PanierItem } from "./types";
import { prixProduit } from "./types";

export function EmpireApp() {
  const [view, setView] = useState<View>({ nom: "accueil" });
  const [panier, setPanier] = useState<PanierItem[]>([]);
  const [panierOuvert, setPanierOuvert] = useState(false);
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [lbIndex, setLbIndex] = useState<number | null>(null);

  const marquer = view.nom === "marque" ? MARQUES.find((m) => m.slug === view.slug) : undefined;

  const nav = useCallback((v: View) => {
    setView(v);
    setMenuOuvert(false);
    setLbIndex(null);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const annoncer = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 2600);
  }, []);

  /* ─── Panier ─── */
  const ajouter = useCallback(
    (item: Omit<PanierItem, "qte">) => {
      setPanier((prev) => {
        const ex = prev.find((i) => i.id === item.id);
        if (ex) return prev.map((i) => (i.id === item.id ? { ...i, qte: i.qte + 1 } : i));
        return [...prev, { ...item, qte: 1 }];
      });
      annoncer(item.nom + " rejoint le panier impérial");
    },
    [annoncer]
  );

  const ajouterProduit = useCallback(
    (slug: string, nomMarque: string, num: number, nomProduit: string, image: string) => {
      ajouter({ id: slug + "/" + num, nom: nomProduit, marque: nomMarque, image, prix: prixProduit(num) });
    },
    [ajouter]
  );

  const ajouterPack = useCallback(
    (idx: number) => {
      const p = PACKS[idx];
      const prix = [79.9, 129.9, 189.9, 34.9][idx];
      if (prix === undefined) return;
      ajouter({ id: "pack/" + idx, nom: p.nom, marque: "L'EMPIRE", image: p.file, prix });
    },
    [ajouter]
  );

  const changerQte = useCallback((id: string, delta: number) => {
    setPanier((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qte: Math.max(0, i.qte + delta) } : i))
        .filter((i) => i.qte > 0)
    );
  }, []);

  const supprimer = useCallback((id: string) => {
    setPanier((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const commander = useCallback(() => {
    setPanier([]);
    setPanierOuvert(false);
    annoncer("Commande impériale enregistrée — merci, et que la bénédiction d'Adji Anta Diop vous accompagne");
  }, [annoncer]);

  /* ─── Lightbox ─── */
  const navLightbox = useCallback(
    (dir: 1 | -1) => {
      if (!marquer || lbIndex === null) return;
      const n = marquer.produits.length;
      setLbIndex((lbIndex + dir + n) % n);
    },
    [marquer, lbIndex]
  );

  useEffect(() => {
    document.body.style.overflow = panierOuvert || lbIndex !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [panierOuvert, lbIndex]);

  const nbArticles = panier.reduce((a, i) => a + i.qte, 0);

  return (
    <div className="empire-bg min-h-screen flex flex-col">
      {/* ═══════════ EN-TÊTE ═══════════ */}
      <header className="sticky top-0 z-50 border-b border-[#D4AF37]/25 bg-[#03150d]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <button
            onClick={() => nav({ nom: "accueil" })}
            className="flex items-center gap-2.5 group focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 rounded-lg px-1"
            aria-label="Retour à l'accueil de l'Empire"
          >
            <Crown className="h-6 w-10" />
            <span className="font-display text-xl md:text-2xl font-black tracking-wide gold-text">
              L&rsquo;EMPIRE
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1" aria-label="Navigation principale">
            {(
              [
                ["accueil", "Accueil"],
                ["sites", "Les 5 Sites"],
                ["marques", "13 Marques"],
                ["packs", "Packs E-commerce"],
                ["portails", "Portails"],
                ["signature", "La Signature"],
              ] as const
            ).map(([nom, label]) => (
              <button
                key={nom}
                onClick={() => (nom === "sites" ? nav({ nom: "site", slug: SITES[0].slug }) : nav({ nom } as View))}
                aria-current={view.nom === nom || (nom === "sites" && view.nom === "site") || (nom === "marques" && view.nom === "marque") ? "page" : undefined}
                className={
                  "px-4 py-2 rounded-lg text-sm tracking-wide transition-colors " +
                  (view.nom === nom || (nom === "sites" && view.nom === "site") || (nom === "marques" && view.nom === "marque")
                    ? "text-[#F6E27A] bg-[#D4AF37]/12"
                    : "text-[#F5EFE0]/70 hover:text-[#F6E27A] hover:bg-[#D4AF37]/8")
                }
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPanierOuvert(true)}
              className="relative p-2.5 rounded-xl border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37]/12 transition-colors"
              aria-label={"Ouvrir le panier (" + nbArticles + " articles)"}
            >
              <ShoppingBag className="h-5 w-5" />
              {nbArticles > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-gradient-to-br from-[#F6E27A] to-[#D4AF37] text-[#170d02] text-[11px] font-bold flex items-center justify-center">
                  {nbArticles}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOuvert((v) => !v)}
              className="md:hidden p-2.5 rounded-xl border border-[#D4AF37]/40 text-[#F6E27A]"
              aria-label="Ouvrir le menu"
              aria-expanded={menuOuvert}
            >
              {menuOuvert ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOuvert && (
          <nav className="md:hidden border-t border-[#D4AF37]/20 bg-[#03150d]/95 px-4 py-3 space-y-1" aria-label="Navigation mobile">
            {(
              [
                ["accueil", "Accueil"],
                ["sites", "Les 5 Sites"],
                ["marques", "13 Marques"],
                ["packs", "Packs E-commerce"],
                ["portails", "Portails"],
                ["signature", "La Signature"],
              ] as const
            ).map(([nom, label]) => (
              <button
                key={nom}
                onClick={() => (nom === "sites" ? nav({ nom: "site", slug: SITES[0].slug }) : nav({ nom } as View))}
                className={
                  "block w-full text-left px-4 py-3 rounded-lg text-sm " +
                  (view.nom === nom || (nom === "sites" && view.nom === "site")
                    ? "text-[#F6E27A] bg-[#D4AF37]/12"
                    : "text-[#F5EFE0]/75 hover:bg-[#D4AF37]/8")
                }
              >
                {label}
              </button>
            ))}
          </nav>
        )}
      </header>

      {/* ═══════════ CONTENU ═══════════ */}
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={view.nom + (view.nom === "marque" ? view.slug : "")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {view.nom === "accueil" && <Accueil onNav={nav} onAddProduit={ajouterProduit} />}
            {view.nom === "marques" && <MarquesView onOpen={(slug) => nav({ nom: "marque", slug })} />}
            {view.nom === "marque" && marquer && (
              <MarqueDetail
                marque={marquer}
                onBack={() => nav({ nom: "marques" })}
                onAdd={(p) => ajouterProduit(marquer.slug, marquer.nom, p.num, p.name, p.file)}
                onOpenLightbox={(i) => setLbIndex(i)}
              />
            )}
            {view.nom === "site" && (
              <SitePage
                siteSlug={view.slug}
                onNav={nav}
                onAddProduit={ajouterProduit}
                onAddPack={ajouterPack}
              />
            )}
            {view.nom === "packs" && <PacksView onAddPack={ajouterPack} />}
            {view.nom === "portails" && <PortailsView />}
            {view.nom === "signature" && <SignatureView />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ═══════════ PIED DE PAGE ═══════════ */}
      <footer className="mt-auto border-t border-[#D4AF37]/25 bg-[#03150d]/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <Crown className="h-6 w-10" />
                <span className="font-display text-2xl font-black gold-text">L&rsquo;EMPIRE</span>
              </div>
              <p className="text-sm text-[#F5EFE0]/55 leading-relaxed">
                La boutique impériale de Son Excellence Président Ndongo Ndiaye —
                13 marques, 405 créations de prestige, une seule promesse.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-4">Les échoppes</p>
              <ul className="space-y-2">
                {MARQUES.slice(0, 6).map((m) => (
                  <li key={m.slug}>
                    <button
                      onClick={() => nav({ nom: "marque", slug: m.slug })}
                      className="text-sm text-[#F5EFE0]/60 hover:text-[#F6E27A] transition-colors"
                    >
                      {m.nom}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => nav({ nom: "marques" })}
                    className="text-sm text-[#F6E27A] hover:underline"
                  >
                    Voir les 13 marques →
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-4">La promesse</p>
              <p className="font-display italic text-lg text-[#F5EFE0]/80 leading-relaxed">
                « {SIGNATURE} »
              </p>
              <p className="text-xs text-[#F5EFE0]/40 mt-4">
                Couronne royale, ruban de soie ivoire et signature complète sur
                chaque étiquette de l&rsquo;Empire.
              </p>
            </div>
          </div>
          <div className="gold-line my-8" />
          {/* Réseau des 5 domaines — chaque domaine a son site déroulé */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs mb-6">
            <span className="tracking-[0.25em] uppercase text-[#D4AF37]">Le réseau</span>
            {SITES.map((s) => (
              <button
                key={s.slug}
                onClick={() => nav({ nom: "site", slug: s.slug })}
                className={
                  "hover:text-[#F6E27A] transition-colors underline-offset-4 hover:underline " +
                  (view.nom === "site" && view.slug === s.slug ? "text-[#F6E27A]" : "text-[#F5EFE0]/55")
                }
              >
                {s.domaine}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#F5EFE0]/40">
            <p>L&rsquo;EMPIRE — Boutique de démonstration · Aucune transaction réelle</p>
            <p className="font-display italic text-[#F6E27A]/70">{SIGNATURE}</p>
          </div>
        </div>
      </footer>

      {/* ═══════════ SURCOUCHES ═══════════ */}
      {marquer && lbIndex !== null && (
        <ProductLightbox
          marque={marquer}
          index={lbIndex}
          onClose={() => setLbIndex(null)}
          onNav={navLightbox}
          onAdd={(p) => ajouterProduit(marquer.slug, marquer.nom, p.num, p.name, p.file)}
        />
      )}
      <PanierDrawer
        open={panierOuvert}
        items={panier}
        onClose={() => setPanierOuvert(false)}
        onQty={changerQte}
        onRemove={supprimer}
        onCommander={commander}
      />
      <ChatbotImperial />
      <FlashToast message={flash} />
    </div>
  );
}
